// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.

import React from 'react';
import { ActionableNotification, InlineLoading } from '@carbon/react';
import { useEquipmentTelemetry } from '../../hooks/useEquipmentTelemetry';
import { useShellStore } from '../../stores/shellStore';
import MetricLineChart from '../common/MetricLineChart';
import { StaleDataNotice } from '../common/StaleDataNotice';

interface AlarmTelemetryProps {
  siteId?: string;
  equipmentId?: string;
}

/**
 * Past-15m equipment telemetry, rendered as one Carbon line chart per metric.
 *
 * The non-chart states are deliberately different. While the fetch is in
 * flight the card renders with a loading indicator, so the operator knows
 * telemetry is coming rather than watching charts appear from nowhere. A failed
 * fetch says so, with Retry — a 500 is not "no telemetry". Once it settles with
 * genuinely nothing to show (missing ids, 404, empty result) the card
 * disappears entirely — telemetry is a graceful enhancement of the alarm detail
 * page, and an empty card claiming a section would be worse than no section.
 */
export default function AlarmTelemetry({ siteId, equipmentId }: AlarmTelemetryProps) {
  const { series, loading, error, refetch } = useEquipmentTelemetry(siteId, equipmentId);
  const theme = useShellStore(s => s.theme);

  if (loading) {
    return (
      <section className="ald-card" aria-busy="true" data-testid="alarm-telemetry">
        <div className="ald-card-title">Telemetry · past 15 mins</div>
        <InlineLoading className="hs-bg-loading" description="Loading telemetry…" status="active" />
      </section>
    );
  }

  if (error && series.length === 0) {
    return (
      <section className="ald-card" data-testid="alarm-telemetry">
        <div className="ald-card-title">Telemetry · past 15 mins</div>
        <ActionableNotification
          kind="error"
          lowContrast
          role="status"
          title="Telemetry unavailable"
          subtitle={error.message}
          actionButtonLabel="Retry"
          onActionButtonClick={refetch}
          hideCloseButton
        />
      </section>
    );
  }

  if (series.length === 0) return null;

  return (
    <section className="ald-card" data-testid="alarm-telemetry">
      <div className="ald-card-title">Telemetry · past 15 mins</div>
      {error && <StaleDataNotice what="telemetry" error={error} onRetry={refetch} />}
      <div className="ald-telemetry-charts">
        {series.map(s => (
          <MetricLineChart key={s.name} series={s} theme={theme} />
        ))}
      </div>
    </section>
  );
}
